import createDOMPurify from 'dompurify';
import { splitLinks } from './linkify';

/**
 * DV-311 rich-text compliance messages: the HTML a message body may contain.
 *
 * The allowlist mirrors the backend's jsoup safelist exactly (see the DV-311 rich-text contract),
 * so what the admin previews is what members get. The backend sanitizes again on save; this copy
 * keeps the preview honest and the payload small.
 */

export const BODY_MAX = 100000;

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li', 'blockquote', 'a', 'span',
];

// Which tag may carry which attribute. `style` is further filtered per property below.
const TAG_ATTRIBUTES = {
  href: ['a'],
  start: ['ol'],
  style: ['p', 'h2', 'h3', 'h4', 'li', 'blockquote', 'span'],
};

const SAFE_HREF = /^(?:https?|mailto):/i;
const LINK_TARGET = '_blank';
const LINK_REL = 'noopener noreferrer nofollow';

const BLOCKS = ['p', 'h2', 'h3', 'h4', 'blockquote'];
const isByte = (n) => Number(n) >= 0 && Number(n) <= 255;

/**
 * Per CSS property: the tags it is allowed on, and a function returning the value to keep
 * (normalised) or null to drop the declaration.
 */
const STYLE_RULES = {
  'text-align': {
    tags: [...BLOCKS, 'li'],
    value: (v) => (['left', 'center', 'right', 'justify'].includes(v) ? v : null),
  },
  'margin-left': {
    tags: BLOCKS,
    value: (v) => {
      const m = /^(\d+)em$/.exec(v);
      return m && Number(m[1]) >= 1 && Number(m[1]) <= 12 ? `${Number(m[1])}em` : null;
    },
  },
  color: {
    tags: ['span'],
    value: (v) => {
      if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(v)) return v;
      const m = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(v);
      return m && [m[1], m[2], m[3]].every(isByte)
        ? `rgb(${Number(m[1])}, ${Number(m[2])}, ${Number(m[3])})`
        : null;
    },
  },
  'font-size': {
    tags: ['span'],
    value: (v) => {
      const m = /^(\d+)px$/.exec(v);
      return m && Number(m[1]) >= 10 && Number(m[1]) <= 36 ? `${Number(m[1])}px` : null;
    },
  },
};

/**
 * Keeps only the declarations the contract allows on `tag`, e.g.
 * filterStyle('span', 'color: #C00; font-family: Arial') -> 'color: #c00'. Returns '' when
 * nothing survives. A repeated property keeps its last valid value, as CSS would.
 */
export const filterStyle = (tag, style) => {
  const kept = new Map();
  String(style || '').split(';').forEach((declaration) => {
    const colon = declaration.indexOf(':');
    if (colon < 0) return;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const rule = STYLE_RULES[property];
    if (!rule || !rule.tags.includes(tag)) return;
    const value = rule.value(declaration.slice(colon + 1).trim().toLowerCase());
    if (value) kept.set(property, value);
  });
  return [...kept].map(([property, value]) => `${property}: ${value}`).join('; ');
};

// A private DOMPurify instance, so these hooks never affect anyone else's sanitizing.
const purifier = createDOMPurify(window);

purifier.addHook('uponSanitizeAttribute', (node, data) => {
  const tag = node.nodeName.toLowerCase();
  const name = data.attrName;
  if (!(TAG_ATTRIBUTES[name] || []).includes(tag)) {
    data.keepAttr = false;
    return;
  }
  const value = String(data.attrValue || '').trim();
  if (name === 'href') {
    // Only http(s) and mailto links; anything else loses its href and keeps its text.
    if (!SAFE_HREF.test(value)) data.keepAttr = false;
    else data.attrValue = value;
  } else if (name === 'start') {
    if (/^\d+$/.test(value) && Number(value) > 0) data.attrValue = String(Number(value));
    else data.keepAttr = false;
  } else if (name === 'style') {
    const style = filterStyle(tag, value);
    if (style) data.attrValue = style;
    else data.keepAttr = false;
  }
});

purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName.toLowerCase() === 'a') {
    node.setAttribute('target', LINK_TARGET);
    node.setAttribute('rel', LINK_REL);
  }
});

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: Object.keys(TAG_ATTRIBUTES),
  // The hook above decides on hrefs; this only stops DOMPurify's own default from widening it.
  // DOMPurify tests every attribute that is not "URI safe" against this regexp, so `start`
  // (validated in the hook) is declared URI safe.
  ALLOWED_URI_REGEXP: SAFE_HREF,
  ADD_URI_SAFE_ATTR: ['start'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  // Removed tags keep their text (e.g. <div>/<font>/<table> pasted from Word or email), except
  // these, which are dropped with everything inside them.
  KEEP_CONTENT: true,
  ADD_FORBID_CONTENTS: ['object', 'embed', 'applet', 'canvas', 'map', 'picture'],
};

/** Sanitizes a message body to the contract's allowlist. Always returns a string. */
export const sanitizeComplianceHtml = (html) => {
  if (!html) return '';
  return purifier.sanitize(String(html), PURIFY_CONFIG);
};

/**
 * True when the HTML shows no text: '', '<p></p>', '<p><br></p>', whitespace or &nbsp; only.
 * Matches the backend's notion of a blank body.
 */
export const isBlankHtml = (html) => {
  if (!html) return true;
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return !doc.body.textContent.replace(/[\s ​﻿]/g, '');
};

const escapeHtml = (text) => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Text with its bare http(s) URLs as links. The text is split on the raw string (so an escaped
// "&lt;" can never be mistaken for part of a URL) and every piece, URLs included, is escaped.
const linkifyEscaped = (text) => splitLinks(text).map((segment) => {
  if (segment.type !== 'url') return escapeHtml(segment.value);
  const url = escapeHtml(segment.value).replace(/"/g, '&quot;');
  return `<a href="${url}" target="${LINK_TARGET}" rel="${LINK_REL}">${url}</a>`;
}).join('');

/**
 * Converts an old plain-text (TEXT) body for the editor: escape it, split on blank lines into
 * paragraphs, turn single line breaks into <br>, and link bare http(s) URLs the way the TEXT
 * rendering did (sentence punctuation after a URL stays text).
 */
export const plainTextToHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((block) => block.replace(/^\n+|\n+$/g, ''))
    .filter((block) => block.trim())
    .map((block) => `<p>${linkifyEscaped(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

/**
 * For paste into the editor: keeps the pasted structure (the editor's schema decides which
 * elements survive) but filters every style attribute to the contract, so pasted Word or email
 * styling never shows formatting that saving would then drop.
 */
export const filterPastedStyles = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  doc.body.querySelectorAll('[style]').forEach((el) => {
    const style = filterStyle(el.nodeName.toLowerCase(), el.getAttribute('style'));
    if (style) el.setAttribute('style', style);
    else el.removeAttribute('style');
  });
  return doc.body.innerHTML;
};
